import { type Stripe, type StripeElements } from '@stripe/stripe-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isError } from 'lodash';

import { useTrackChangedPaymentMethod } from '@air/analytics';
import { Subscriptions } from '@air/api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { createCardToken } from '~/components/AirStripe/createCardToken';
import { WORKSPACE_LOCKED_PAYMENT_UPDATED } from '~/constants/localStorageKeys';
import { getSubscriptionKey } from '~/swr-hooks/subscriptions/useSubscription';
import { getSubscriptionPlanKey } from '~/swr-hooks/subscriptions/useSubscriptionPlan';

type UpdatePaymentMethodParams = {
  stripe: Stripe | null;
  elements: StripeElements | null;
  workspaceId: string;
};

export const useUpdatePaymentMethodMutation = () => {
  const queryClient = useQueryClient();
  const { trackChangedPaymentMethod } = useTrackChangedPaymentMethod();

  return useMutation<void, Error, UpdatePaymentMethodParams>({
    mutationFn: async ({ stripe, elements, workspaceId }) => {
      if (!stripe || !elements) {
        throw new Error('Payment system is still loading. Please try again.');
      }

      if (!workspaceId) {
        throw new Error('No workspace selected. Please refresh and try again.');
      }

      try {
        const { token, error } = await createCardToken({ stripe, elements });

        if (error) {
          throw new Error(error.message);
        }

        if (isError(token) || !token) {
          throw new Error(typeof token === 'string' ? token : 'A token failed to be created');
        }

        await Subscriptions.updateStripeSubscriptionPaymentMethod({ workspaceId, token });

        // Track analytics
        trackChangedPaymentMethod();

        // Store payment update timestamp with workspace ID
        const storageKey = `${WORKSPACE_LOCKED_PAYMENT_UPDATED}/${workspaceId}`;
        localStorage.setItem(storageKey, new Date().toISOString());
      } catch (error) {
        const reportedError = reportErrorToBugsnag({
          error,
          context: `Failed to update payment method`,
        });
        throw new Error(reportedError.message);
      }
    },
    onSuccess: (_data, { workspaceId }) => {
      // Invalidate subscription queries. Fire-and-forget: a failed refetch just leaves stale
      // cached data momentarily, it doesn't affect the mutation's own success/error outcome.
      void queryClient.invalidateQueries({ queryKey: getSubscriptionKey(workspaceId) });
      void queryClient.invalidateQueries({ queryKey: getSubscriptionPlanKey(workspaceId) });
    },
  });
};
